import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RadarRoot } from "./RadarRoot";
import { AuthProvider } from "./auth";
import { AdminI18nProvider } from "./lib/i18n";
import { initPersonalize } from "./lib/personalize";
import { installTokens } from "./theme";
import "./styles.css";
import "./personalize.css";
import "./shell/mobile/mobile.css";

// Shared design tokens → CSS variables before first paint (hex lives only in
// ui-tokens; see theme.ts).
installTokens();
// Wallpaper + avatar choices (device-level) before first paint too.
initPersonalize();

/**
 * Stale-chunk recovery after a deploy.
 *
 * Apps are lazy chunks, and every build hashes them anew. A tab left open
 * across a deploy still holds the OLD index bundle, so opening an app requests
 * a chunk that no longer exists. The asset host answers a missing path with
 * index.html (`not_found_handling = "single-page-application"`), so the import
 * fails with "'text/html' is not a valid JavaScript MIME type" and the app
 * silently does not open.
 *
 * Vite fires `vite:preloadError` for exactly this. Reload once to pick up the
 * new index; the sessionStorage flag stops a reload loop if the chunk is
 * genuinely gone rather than merely stale.
 */
const RELOADED_KEY = "radar.stale-chunk-reloaded";
window.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(RELOADED_KEY)) return; // already tried; let it surface
  event.preventDefault();
  sessionStorage.setItem(RELOADED_KEY, "1");
  window.location.reload();
});
// A load that got all the way here is healthy, so arm the next recovery.
window.addEventListener("load", () => sessionStorage.removeItem(RELOADED_KEY));

// Retries off — the api-client already retries the only retryable statuses.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminI18nProvider>
          <RadarRoot />
        </AdminI18nProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
